import { SnackbarProvider } from 'notistack';
import { PostHogProvider } from 'posthog-js/react';
import React from 'react';
import { Provider as ReduxProvider } from 'react-redux';

import {
  FlagContextType,
  FlagsContext,
  PortalConfigContext,
  RouteContext,
  SessionData,
} from 'src/context';
import { useDeepLinkNavigation } from 'src/hooks/useDeepLinkNavigation';
import { useIsAssemblyBrandEnabled } from 'src/hooks/useIsAssemblyBrandEnabled';
import { initI18nResources } from 'src/i18n/i18next.config';
import { ConfigureAuth } from 'src/legacy/components/Auth/ConfigureAuth';
import Notifier from 'src/legacy/components/Notifier/Notifier';

import { AppProviders } from './AppProviders';

interface UnauthAppProps {
  context: {
    insertCss: (...styles: any[]) => () => void;
    pathname: string;
    query: Record<string, any>;
    isMobile: boolean;
    portalConfig: any;
    sessionData: SessionData;
    store: any;
    flags: FlagContextType;
    posthogBootstrap: {
      distinctId: string;
      featureFlags: Record<string, boolean | string>;
    };
    stripeContext: { publishableKey: string };
    params: Record<string, string>;
    posthogConfig: {
      apiKey: string;
      host: string;
    };
  };
  children: React.ReactNode;
}

const UnauthApp: React.FC<UnauthAppProps> = ({ context, children }) => {
  // NOTE: If you need to add or modify header, footer etc. of the app,
  // please do that inside the Layout component.
  const {
    insertCss,
    pathname,
    query,
    isMobile,
    portalConfig,
    sessionData,
    store,
    flags,
    posthogBootstrap,
    params,
    posthogConfig,
  } = context;

  const { isAssemblyBrandEnabled } = useIsAssemblyBrandEnabled();

  // Handle non-auth deep links (e.g. assembly://settings) while on the login page.
  useDeepLinkNavigation();

  const routeContext = React.useMemo(
    () => ({ pathname, query, isMobile, sessionData, params }),
    [pathname, query, isMobile, sessionData, params],
  );

  // Feature flags state - starts with server-bootstrapped flags, syncs when PostHog is ready
  const { distinctId, featureFlags } = posthogBootstrap ?? {};

  const posthogOptions = React.useMemo(() => {
    return {
      api_host: posthogConfig.host,
      ui_host: 'https://us.posthog.com',
      defaults: '2025-11-30' as const,
      // Bootstrap flags from server to avoid client-side /flags API call
      bootstrap: {
        distinctID: distinctId,
        featureFlags: featureFlags,
      },
      // Disable fetching flags on first load since we bootstrap from server
      advanced_disable_feature_flags_on_first_load: true,
    } as const;
  }, [posthogConfig, distinctId, featureFlags]);

  // inject the custom labels into the i18n instance
  // and instantiate the i18n resources
  initI18nResources({
    customLabels: portalConfig.features.customLabels,
    isAssembly: isAssemblyBrandEnabled,
  });

  return (
    <PostHogProvider apiKey={posthogConfig.apiKey} options={posthogOptions}>
      <FlagsContext.Provider value={flags}>
        <PortalConfigContext.Provider value={portalConfig}>
          <AppProviders insertCss={insertCss}>
            <ReduxProvider store={store}>
              <RouteContext.Provider value={routeContext}>
                <SnackbarProvider
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  maxSnack={2}
                >
                  <Notifier />
                  <ConfigureAuth />
                  {React.Children.only(children)}
                </SnackbarProvider>
              </RouteContext.Provider>
            </ReduxProvider>
          </AppProviders>
        </PortalConfigContext.Provider>
      </FlagsContext.Provider>
    </PostHogProvider>
  );
};

export default UnauthApp;
